import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
// Sonner Toaster lazy-loaded — its renderer + portal logic only matters
// the first time something calls `toast()`. Sonner's `toast()` function
// queues calls until the Toaster mounts, so toasts fired before the
// chunk lands still render once it does. Saves ~3-4 KB gzipped from
// main.js for the 99 %+ of page loads that never trigger a toast.
const Toaster = lazy(() =>
  import("sonner").then((m) => ({ default: m.Toaster }))
);
import api from "@/lib/api_client";
import { I18nProvider, SITE_LANGS, VALID_LANG_CODES, langFromPath } from "@/lib/i18n";
import SEO_ROUTES from "@/data/seo_routes.json";

// Landing eagerly bundled — fastest TTI on the SEO-critical homepage.
import Landing from "@/pages/Landing";

// All other routes lazy-loaded to shrink the homepage bundle so Google
// rates Core Web Vitals favorably (smaller LCP + lower TBT on Landing).
const Generator = lazy(() => import("@/pages/Generator"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PublicStats = lazy(() => import("@/pages/PublicStats"));
const SiteSection = lazy(() => import("@/pages/SiteSection"));
const Contact = lazy(() => import("@/pages/Contact"));
const AdminContacts = lazy(() => import("@/pages/AdminContacts"));
const AdminBugBoard = lazy(() => import("@/pages/AdminBugBoard"));
const AdminDirectory = lazy(() => import("@/pages/AdminDirectory"));
const EmailStudio = lazy(() => import("@/pages/EmailStudio"));
const AdminSubscribers = lazy(() => import("@/pages/AdminSubscribers"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const SeoTool = lazy(() => import("@/pages/SeoTool"));
const SeoToolsIndex = lazy(() => import("@/pages/SeoToolsIndex"));
const AiAudit = lazy(() => import("@/pages/AiAudit"));
const WidgetGallery = lazy(() => import("@/pages/WidgetGallery"));
const WidgetLanding = lazy(() => import("@/pages/WidgetLanding"));
// v60.99.100 — Phase 2 programmatic SEO: /{platform} hub + /{platform}/{widget}
// long-tail pages ("live traffic widget for WordPress" etc.). Lazy so the
// pSEO chunk never lands on any other route.
const PlatformHub = lazy(() => import("@/pages/PlatformHub"));
const PlatformWidgetLanding = lazy(() => import("@/pages/PlatformWidgetLanding"));
const PlatformCompare = lazy(() => import("@/pages/PlatformCompare"));
const WidgetRival = lazy(() => import("@/pages/WidgetRival"));
const PlatformNicheLanding = lazy(() => import("@/pages/PlatformNicheLanding"));
// v60.99.101 — real 404. Before this the app had NO catch-all route, so any
// unmatched path (a typo, or a doubled slash like //free-live-traffic-widget)
// matched nothing and rendered a permanently blank white page.
const NotFound = lazy(() => import("@/pages/NotFound"));
const HeatmapDashboard = lazy(() => import("@/pages/HeatmapDashboard"));
const Explore = lazy(() => import("@/pages/Explore"));
const Sitemap = lazy(() => import("@/pages/Sitemap"));
const Movers = lazy(() => import("@/pages/Movers"));
const ProgrammaticPost = lazy(() => import("@/pages/ProgrammaticPost"));
const Milestones = lazy(() => import("@/pages/Milestones"));
const SerpChecker = lazy(() => import("@/pages/SerpChecker"));
const IndexedChecker = lazy(() => import("@/pages/IndexedChecker"));
const MetaTagGenerator = lazy(() => import("@/pages/MetaTagGenerator"));
const SchemaGenerator = lazy(() => import("@/pages/SchemaGenerator"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminBacklinks = lazy(() => import("@/pages/AdminBacklinks"));
const AdminFlywheel = lazy(() => import("@/pages/AdminFlywheel"));
const AdminListings = lazy(() => import("@/pages/AdminListings"));
const AdminGsc = lazy(() => import("@/pages/AdminGsc"));
const AdminRoadmap = lazy(() => import("@/pages/AdminRoadmap"));
const AdminLaunchDocs = lazy(() => import("@/pages/AdminLaunchDocs"));
const AdminSEOFoundation = lazy(() => import("@/pages/AdminSEOFoundation"));
const AdminReferral = lazy(() => import("@/pages/AdminReferral"));
const AdminRankTracker = lazy(() => import("@/pages/AdminRankTracker"));
const AdminPsiGuard = lazy(() => import("@/pages/AdminPsiGuard"));
const AdminSites = lazy(() => import("@/pages/AdminSites"));
const DataSources = lazy(() => import("@/pages/DataSources"));
const TopicHub = lazy(() => import("@/pages/TopicHub"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const FeaturedOn = lazy(() => import("@/pages/FeaturedOn"));
const SampleHeatmap = lazy(() => import("@/pages/SampleHeatmap"));
const VpnBlockOverlay = lazy(() => import("@/components/VpnBlockOverlay"));

const RouteFallback = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <div className="font-mono text-xs uppercase tracking-widest text-zinc-400">Loading…</div>
  </div>
);

// Robust hash-anchor scrolling for the in-page nav links (#widgets,
// #how, #faq, etc). Default browser anchor handling is broken in three
// ways for our SPA:
//   1. Sticky header (h-14/h-16) hides the section's top after scroll.
//   2. Clicking the SAME hash twice does nothing because the URL
//      doesn't change — the user feels "the link is dead".
//   3. Cross-route nav (from /blog → /#faq) lands the user at "/" but
//      ScrollToTop forces scroll-to-top BEFORE Helmet has rendered the
//      target section in the DOM, so the hash is silently dropped.
//
// This handler intercepts ALL anchor clicks whose href is `#foo` AND
// scrolls to `[id="foo"]` with a header-aware offset, regardless of
// the current URL.
const HashAnchorScroller = () => {
  const { hash, pathname } = useLocation();

  // Header offset = 56 px (mobile h-14) or 64 px (md h-16) plus a
  // small breathing margin so the section heading sits below the
  // sticky bar instead of flush against it.
  const _scrollToHash = (h) => {
    if (!h) return;
    const id = h.replace(/^#/, "");
    const el = document.getElementById(id);
    if (!el) return;
    const headerH = window.innerWidth >= 768 ? 64 : 56;
    const y = el.getBoundingClientRect().top + window.scrollY - headerH - 12;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  // v60.99.74 — when hash-nav lands the homepage, every DeferUntilVisible
  // wrapper mounts EAGERLY (isHashNav=true). The #faq wrapper exists in
  // the DOM immediately, so a one-shot _scrollToHash() lands at the
  // wrapper's CURRENT page-y — but the giant sections above (widgets,
  // seo-tools, util-widgets, testimonials, seo-content, free-cta) are
  // still rendering. As they fill in, FAQ shifts ~1200px further down
  // and the user ends up parked above it.
  //
  // Fix: after the initial scroll, schedule a series of re-measure +
  // re-scroll passes at staggered delays. If the target's page-y has
  // moved by more than `threshold` px since the last scroll, re-scroll.
  // Stops early once two consecutive passes show the element stable
  // (i.e. layout settled).
  const _scrollWithReanchor = (h) => {
    if (!h) return;
    _scrollToHash(h);
    const id = h.replace(/^#/, "");
    const delays = [100, 250, 500, 850, 1300, 1900, 2600];
    let lastTopPage = null;
    let stableHits = 0;
    delays.forEach((d) => {
      setTimeout(() => {
        const el = document.getElementById(id);
        if (!el) return;
        const topPage = el.getBoundingClientRect().top + window.scrollY;
        if (lastTopPage !== null && Math.abs(topPage - lastTopPage) < 12) {
          stableHits += 1;
        } else {
          stableHits = 0;
          _scrollToHash(h);
        }
        lastTopPage = topPage;
        // After two consecutive stable measurements, layout has settled.
        // Future timeouts still fire but become no-ops.
        if (stableHits >= 2) return;
      }, d);
    });
  };

  // (a) Intercept clicks anywhere on any <a href="#..."> — keeps the
  //     same-hash-twice case working AND adds the header offset.
  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!href.startsWith("#")) return;
      const id = href.slice(1);
      if (!id) return;
      // Always claim the click so the browser's native (broken) anchor
      // scroll never runs in parallel with our handler.
      e.preventDefault();
      try {
        window.history.replaceState(null, "", href);
      } catch (_) { /* SecurityError in cross-origin iframes — ignore */ }

      const target = document.getElementById(id);
      if (target) {
        _scrollWithReanchor(href);
        return;
      }
      // Target isn't mounted yet — almost always because it lives inside
      // a `<DeferUntilVisible>` placeholder that hasn't entered the
      // viewport (#how → Steps, #faq → FAQ, etc.). Dispatch the
      // expand-deferred signal so every placeholder mounts its children,
      // then retry the scroll once React has flushed the new tree.
      window.dispatchEvent(new CustomEvent("feedpulse:expand-deferred"));
      let attempts = 0;
      const retry = () => {
        attempts += 1;
        const el = document.getElementById(id);
        if (el) {
          _scrollWithReanchor(href);
          return;
        }
        // v60.99.73 — bumped from 10 to 60 frames (~1s) to cover the
        // first-daily-visit case where the lazy FAQ chunk takes 300-800ms
        // to download on a cold cache. Previously the 10-frame budget
        // ran out before the chunk arrived, so the first FAQ click did
        // nothing and the user had to retry 2-3 times. The DeferUntilVisible
        // wrapper now also carries the id, so for hash targets that map
        // to a wrapper this branch fires only as a defense-in-depth.
        if (attempts < 60) {
          requestAnimationFrame(retry);
        }
      };
      requestAnimationFrame(retry);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // (b) When pathname OR hash changes via React Router (e.g. user
  //     clicks `/#faq` from another page), scroll to the target once
  //     the new page's DOM has settled. If the target lives inside a
  //     deferred section that hasn't mounted yet, dispatch the
  //     expand-deferred signal and poll until it appears (same logic
  //     as the click handler above).
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    let cancelled = false;
    let attempts = 0;

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        _scrollWithReanchor(hash);
        return;
      }
      if (attempts === 0) {
        // First miss → force-mount every deferred section, then poll.
        window.dispatchEvent(new CustomEvent("feedpulse:expand-deferred"));
      }
      attempts += 1;
      // v60.99.73 — bumped from 10 to 60 frames (~1s) to cover cold-cache
      // lazy chunk downloads on cross-route hash navigation (e.g.
      // /blog → /#faq) where the FAQ chunk takes 300-800ms to arrive.
      if (attempts < 60) {
        requestAnimationFrame(tryScroll);
      }
    };

    // Two-frame initial delay so React has flushed the new route's
    // children before we look for the anchor — same pattern as before.
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(tryScroll);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
    };
  }, [hash, pathname]);

  return null;
};

// Reset scroll to top on every route change. React Router's BrowserRouter
// preserves scroll position by default, which means clicking a blog card
// from the middle of the blog index landed you halfway through the post.
// This fires immediately on pathname change so the user always starts at
// the top of the new page. Uses `instant` so it doesn't fight the browser's
// own back/forward restoration UX on user-driven nav.
//
// IMPORTANT: skips the reset when a hash is present in the URL — that
// case is owned by `HashAnchorScroller`, which scrolls to the anchor
// instead of the top. Without the skip, `/#faq` would briefly flash
// the homepage at scrollY=0 before jumping to the anchor.
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
};

// Fires a server-side page_view event on every client-side route change for
// the homegrown /admin/referral analytics (cookieless: hashes IP → country code
// server-side and discards). v60.99.85 — Google Analytics removed; we rely
// solely on this self-pageview pipeline now. Defers a tick after location
// change so react-helmet-async has time to swap document.title first.
const AnalyticsListener = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // Best-effort self-server-side pageview tracker. Powers the Admin
    // Operator Dashboard's "Page Visits" section. Fire-and-forget;
    // analytics MUST never break the page so we ignore all errors.
    // No-op `search` reference kept to preserve the existing dep array
    // semantics — pageviews fire on both pathname AND query changes.
    void search;
    try {
      let session = localStorage.getItem("fp_pv_session");
      if (!session) {
        session = Math.random().toString(36).slice(2, 14) + Date.now().toString(36);
        try { localStorage.setItem("fp_pv_session", session); } catch (_) { /* ignore */ }
      }
      api.post(`${process.env.REACT_APP_BACKEND_URL}/api/self-pageview`, {
        path: pathname,
        referrer: document.referrer || "",
        session,
      }).catch(() => {});
    } catch (_) { /* ignore */ }
  }, [pathname, search]);
  return null;
};

// Emits <link rel="alternate" hreflang> tags for every supported language
// pointing at the current route's path. This is the W3C-recommended pattern
// for telling Google about language alternates — paired with the per-
// language prerender pipeline it boosts SEO reach by ~12x.
//
// Convention: English (default) at `/path`, other languages at `/{lang}/path`.
// The component reads the current pathname, strips any existing lang prefix
// to get the canonical "language-agnostic" path, then emits 12 alternates +
// 1 x-default pointing at the English version.

// v60.72 — Canonicalize `?lang=xx` query strings to path-prefix form.
//
// Background: Google Search Console reported 92 URLs as "Duplicate
// without user-selected canonical" — pages indexed at both
// `/blog/foo?lang=es` AND `/es/blog/foo`. This was from an older code
// path that emitted ?lang= query strings; the path-prefix style has
// been canonical for a while now, but Googlebot retained the old
// URLs in its index.
//
// Fix: on any route mount, if the URL contains `?lang=xx` where xx is
// a supported language, rewrite the URL to `/{lang}/{path}` (or strip
// the param entirely for English) using `navigate(..., {replace:true})`.
// The replace is critical — it issues a client-side history rewrite
// that Googlebot interprets as "the canonical URL is the new one".
// Combined with the existing `<link rel="canonical">` tags and the
// `Disallow: /*?lang=` rule in robots.txt, Google de-duplicates the
// 92 affected URLs on its next crawl pass.
const LangParamRedirect = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!search) return;
    const params = new URLSearchParams(search);
    const qLang = (params.get("lang") || "").toLowerCase();
    if (!qLang || !VALID_LANG_CODES.has(qLang)) return;
    // Strip the lang param from the query string; keep everything else.
    params.delete("lang");
    const remainingQuery = params.toString();
    // Strip any existing lang prefix from the path so we don't end up
    // with `/es/es/blog/foo` if both forms coexist on the same URL.
    const existingLang = langFromPath(pathname);
    const cleanPath = existingLang
      ? pathname.replace(new RegExp(`^/${existingLang}`), "") || "/"
      : pathname;
    const targetPath = qLang === "en"
      ? cleanPath
      : `/${qLang}${cleanPath === "/" ? "/" : cleanPath}`;
    const finalUrl = remainingQuery
      ? `${targetPath}?${remainingQuery}`
      : targetPath;
    navigate(finalUrl, { replace: true });
  }, [pathname, search, navigate]);
  return null;
};

// English-only surfaces: pSEO platform/vs pages and per-domain /site + /post
// pages. Advertising /{lang}/ twins for them led Google to crawl URLs that
// render English (and, for old test domains, 5xx'd).
const EN_ONLY_PATHS = new Set((SEO_ROUTES.pseo_slugs || []).map((s) => `/${s}`));
const isEnOnlyPath = (p) => EN_ONLY_PATHS.has(p) || p.startsWith("/site/") || p.startsWith("/post/");

const Hreflang = () => {
  const { pathname } = useLocation();
  // Strip leading lang prefix if present so we work with the canonical path.
  const currentLang = langFromPath(pathname);
  const canonical = currentLang
    ? pathname.replace(new RegExp(`^/${currentLang}`), "") || "/"
    : pathname;
  if (isEnOnlyPath(canonical)) return null;
  // Resolve the site origin at render time (works in both SSR snapshots and
  // client navigation). Falls back to relative URLs if window is missing.
  const origin =
    (typeof window !== "undefined" && window.location && window.location.origin) || "";
  const urlFor = (lang) => {
    const clean = canonical === "/" ? "" : canonical;
    if (lang === "en") return `${origin}${canonical}`;
    return `${origin}/${lang}${clean || "/"}`;
  };
  return (
    <Helmet>
      {SITE_LANGS.map((l) => (
        <link key={l.code} rel="alternate" hrefLang={l.code} href={urlFor(l.code)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={urlFor("en")} />
    </Helmet>
  );
};

// All non-/:lang routes — extracted so we can render them twice in <Routes>:
// once at the root and once nested under a /:lang/* prefix. Avoids dual-
// maintenance and ensures every page works both in English and any of the
// 12 supported languages without code duplication.
const appRoutes = (
  <>
    <Route path="/" element={<Landing />} />
    <Route path="generator" element={<Generator />} />
    <Route path="dashboard" element={<Dashboard />} />
    <Route path="site/:slug" element={<PublicStats />} />
    <Route path="site/:slug/:section" element={<SiteSection />} />
    <Route path="contact" element={<Contact />} />
    <Route path="admin/contacts" element={<AdminContacts />} />
    <Route path="admin/bug-board" element={<AdminBugBoard />} />
    <Route path="admin/directory" element={<AdminDirectory />} />
    <Route path="admin/email-studio" element={<EmailStudio />} />
    <Route path="admin/subscribers" element={<AdminSubscribers />} />
    <Route path="blog" element={<Blog />} />
    {/* v60.102 — real paginated archive paths. Query-string pagination
        (?page=N) could not be prerendered, so pages 2+ shipped the /blog
        template with a canonical pointing at /blog. Blog.jsx still redirects
        old ?page=N links here. */}
    <Route path="blog/page/:page" element={<Blog />} />
    <Route path="blog/:slug" element={<BlogPost />} />
    <Route path="tools" element={<SeoToolsIndex />} />
    <Route path="guides" element={<TopicHub />} />
    <Route path="guides/:hubId" element={<TopicHub />} />
    <Route path="rank" element={<SeoTool />} />
    <Route path="da-checker" element={<SeoTool />} />
    <Route path="dr-checker" element={<SeoTool />} />
    <Route path="trust-score" element={<SeoTool />} />
    <Route path="lighthouse" element={<SeoTool />} />
    <Route path="speed" element={<SeoTool />} />
    <Route path="domain-age" element={<SeoTool />} />
    <Route path="backlinks" element={<SeoTool />} />
    <Route path="serp-checker" element={<SerpChecker />} />
    <Route path="indexed-checker" element={<IndexedChecker />} />
    <Route path="meta-tag-generator" element={<MetaTagGenerator />} />
    <Route path="schema-markup-generator" element={<SchemaGenerator />} />
    <Route path="ai-audit" element={<AiAudit />} />
    <Route path="widgets" element={<WidgetGallery />} />
    <Route path="widgets/:id" element={<WidgetGallery />} />
    <Route path="explore" element={<Explore />} />
    <Route path="sitemap" element={<Sitemap />} />
    <Route path="movers" element={<Movers />} />
    <Route path="post/:slug/:topic" element={<ProgrammaticPost />} />
    <Route path="milestones" element={<Milestones />} />
    <Route path="dashboard/heatmap" element={<HeatmapDashboard />} />
    <Route path="free-hit-counter" element={<WidgetLanding />} />
    <Route path="heatmap" element={<WidgetLanding />} />
    <Route path="sample-heatmap" element={<SampleHeatmap />} />
    <Route path="free-weather-widget" element={<WidgetLanding />} />
    <Route path="free-crypto-ticker-widget" element={<WidgetLanding />} />
    <Route path="free-stock-ticker-widget" element={<WidgetLanding />} />
    <Route path="free-countdown-widget" element={<WidgetLanding />} />
    <Route path="free-visitor-globe-widget" element={<WidgetLanding />} />
    <Route path="free-comments-widget" element={<WidgetLanding />} />
    {/* v60.99.99 — two landings that were missing: the flag counter only
        existed as a /generator tab (robots-Disallowed) and the online-now
        counter only existed inside /widgets. Both keyword clusters now
        have an indexable home. The flag-counter slug matches the internal
        links two blog posts were already emitting. */}
    <Route path="free-flag-counter-widget" element={<WidgetLanding />} />
    {/* v60.99.100 — Phase 2 pSEO. Explicit platform prefix (NOT a
        `/:platform/:slug` wildcard) so these routes can never swallow
        an existing top-level page. Adding Shopify/Wix later = two
        more lines here plus one PLATFORMS entry. */}
    <Route path="wordpress" element={<PlatformHub platformId="wordpress" />} />
    {/* v60.99.104 — comparison pages. Static segments outrank the :slug
        route in React Router's ranking, so order here doesn't matter. */}
    <Route path="wordpress/vs-plugins" element={<PlatformCompare platformId="wordpress" />} />
    <Route path="shopify/vs-apps" element={<PlatformCompare platformId="shopify" />} />
    <Route path="wix/vs-app-market-widgets" element={<PlatformCompare platformId="wix" />} />
    <Route path="webflow/vs-apps" element={<PlatformCompare platformId="webflow" />} />
    <Route path="wordpress/:slug" element={<PlatformWidgetLanding platformId="wordpress" />} />
    {/* v60.99.102 — Phase 2 pSEO, platform #2. */}
    <Route path="shopify" element={<PlatformHub platformId="shopify" />} />
    <Route path="shopify/:slug" element={<PlatformWidgetLanding platformId="shopify" />} />
    {/* v60.99.103 — Phase 2 pSEO, platform #3. */}
    <Route path="wix" element={<PlatformHub platformId="wix" />} />
    <Route path="wix/:slug" element={<PlatformWidgetLanding platformId="wix" />} />
    {/* v60.99.104 — Phase 2 pSEO, platform #4. */}
    <Route path="webflow" element={<PlatformHub platformId="webflow" />} />
    <Route path="webflow/:slug" element={<PlatformWidgetLanding platformId="webflow" />} />
    {/* v60.99.105 — Phase 2 pSEO, platform #5. */}
    <Route path="squarespace" element={<PlatformHub platformId="squarespace" />} />
    <Route path="squarespace/vs-extensions" element={<PlatformCompare platformId="squarespace" />} />
    <Route path="squarespace/:slug" element={<PlatformWidgetLanding platformId="squarespace" />} />
    {/* v60.99.106 — Phase 2 pSEO, platform #6 (closes the Oct 30 row). */}
    <Route path="ghost" element={<PlatformHub platformId="ghost" />} />
    <Route path="ghost/vs-integrations" element={<PlatformCompare platformId="ghost" />} />
    <Route path="ghost/:slug" element={<PlatformWidgetLanding platformId="ghost" />} />
    {/* v60.99.109 — one dynamic route for all 18 /vs/<rival> pages; the
        component renders <NotFound/> for an unknown slug. */}
    <Route path="vs/:rival" element={<WidgetRival />} />
    {/* v60.99.109 — pSEO #1 test cell: 3 platforms x 8 niches = 24 pages. */}
    <Route path="widget-for/:platform/:niche" element={<PlatformNicheLanding />} />
    {/* v60.99.107 — Phase 2 pSEO, platform #7. */}
    <Route path="blogger" element={<PlatformHub platformId="blogger" />} />
    <Route path="blogger/vs-gadgets" element={<PlatformCompare platformId="blogger" />} />
    <Route path="blogger/:slug" element={<PlatformWidgetLanding platformId="blogger" />} />
    <Route path="online-now-widget" element={<WidgetLanding />} />
    <Route path="free-flag-counter" element={<Navigate to="/free-flag-counter-widget" replace />} />
    <Route path="free-online-now-widget" element={<Navigate to="/online-now-widget" replace />} />
    <Route path="free-verified-traffic-badge" element={<WidgetLanding />} />
    <Route path="free-youtube-live-widget" element={<WidgetLanding />} />
    <Route path="free-twitch-live-widget" element={<WidgetLanding />} />
    {/* v60.98 — SEO rename pill → widget. Old slugs kept as permanent
        client-side redirects (Navigate replace) so existing inbound
        links + bookmarks still resolve. The new slug is the canonical
        URL in sitemap.xml + every internal link. Googlebot follows
        these JS-driven redirects and the new URL takes over the
        old URL's rankings within 2-4 weeks. */}
    <Route path="free-youtube-live-pill" element={<Navigate to="/free-youtube-live-widget" replace />} />
    <Route path="free-twitch-live-pill" element={<Navigate to="/free-twitch-live-widget" replace />} />
    <Route path="free-ai-site-chat" element={<WidgetLanding />} />
    <Route path="free-page-speed-badge" element={<WidgetLanding />} />
    <Route path="free-github-stars-badge" element={<WidgetLanding />} />
    <Route path="free-npm-downloads-badge" element={<WidgetLanding />} />
    <Route path="free-discord-members-widget" element={<WidgetLanding />} />
    <Route path="free-discord-members-pill" element={<Navigate to="/free-discord-members-widget" replace />} />
    {/* v60.99.62 — orphan-URL redirects for legacy / external-link
        targets that were never wired as real routes. Without these,
        the SPA fallback renders the homepage but PSI flags it as
        `canonical` mismatch (URL doesn't match the rendered
        canonical href) → costs 8 SEO points per orphan URL. */}
    {/* v60.99.63 — /free-live-traffic-widget now renders the Landing
        component directly with `pathOverride` so canonical is
        self-referential (PSI doesn't follow client-side Navigate
        for canonical audits → previous Navigate→/ approach left
        SEO at 92). seoKey="live_traffic_widget" gives the page a
        distinct SERP title/description, avoiding duplicate-content
        overlap with the homepage's Feedjit-alternative angle. */}
    {/* v60.99.120 — /free-live-traffic-widget rendered the ENTIRE homepage
        with a self-referential canonical, i.e. two self-canonical URLs
        serving byte-identical 346-word bodies. Google resolved that duplicate
        cluster by indexing NEITHER: an exact-phrase search for the shared H1
        returned zero results. The homepage must own the head term, so this
        alias now redirects to "/" instead of cloning it. The v60.99.63 PSI
        note below is kept for history — an 8-point canonical audit quibble is
        worth far less than an indexable homepage. */}
    <Route path="free-live-traffic-widget" element={<Navigate to="/" replace />} />
    <Route path="free-online-visitors" element={<Navigate to="/free-hit-counter" replace />} />
    <Route path="free-online-visitors-counter" element={<Navigate to="/free-hit-counter" replace />} />
    <Route path="data-sources" element={<DataSources />} />
    <Route path="privacy" element={<Privacy />} />
    {/* v60.99.111 — public trust page: every directory that listed FeedPulse. */}
    <Route path="featured-on" element={<FeaturedOn />} />
    {/* Operator-only — robots-blocked + not in sitemap. Auth happens in-page. */}
    <Route path="admin/quota" element={<AdminDashboard />} />
    <Route path="admin/backlinks" element={<AdminBacklinks />} />
    <Route path="admin/flywheel" element={<AdminFlywheel />} />
    <Route path="admin/listings" element={<AdminListings />} />
    <Route path="admin/gsc" element={<AdminGsc />} />
    <Route path="admin/roadmap" element={<AdminRoadmap />} />
    <Route path="admin/docs" element={<AdminLaunchDocs />} />
    <Route path="admin/seo-foundation" element={<AdminSEOFoundation />} />
    <Route path="admin/referral" element={<AdminReferral />} />
    <Route path="admin/rank-tracker" element={<AdminRankTracker />} />
    <Route path="admin/psi-guard" element={<AdminPsiGuard />} />
    <Route path="admin/sites" element={<AdminSites />} />
    {/* v60.99.101 — catch-all MUST stay last. Without it an unmatched path
        rendered nothing at all (blank white page) instead of a 404. */}
    <Route path="*" element={<NotFound />} />
  </>
);

// Wrapper used for the /:lang/* routes — validates that the prefix is one
// of our 12 supported langs; if not, redirect to the English version.
const LangGuard = () => {
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);
  if (!lang || !VALID_LANG_CODES.has(lang)) {
    // v60.99.101 — `/:lang/*` out-ranks the `*` catch-all in React Router, so
    // a single-segment typo like /this-does-not-exist lands here. Stripping
    // the segment left "" and bounced it to the homepage with a 200, which is
    // a soft-404 Google penalises. Only strip when there IS a remaining path.
    const rest = pathname.replace(/^\/[^/]+/, "");
    if (!rest || rest === "/") return <NotFound />;
    return <Navigate to={rest} replace />;
  }
  // Valid lang — render the same route tree. React Router's relative <Route>
  // syntax means children under <Route path="/:lang/*"> match the rest of
  // the URL exactly like the root routes. The I18nProvider already detects
  // the lang from the path on its own.
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>{appRoutes}</Routes>
    </Suspense>
  );
};

// v60.99.101 — collapse duplicate slashes before routing. `//free-live-traffic-widget`
// is a real URL people paste (and that external sites emit); React Router treats it
// as its own pathname, matches nothing, and the page goes blank. Normalise once,
// replace the history entry, and the correct page renders.
const CollapseSlashes = () => {
  const { pathname, search, hash } = useLocation();
  if (!/\/{2,}/.test(pathname)) return null;
  const clean = pathname.replace(/\/{2,}/g, "/") || "/";
  return <Navigate to={`${clean}${search}${hash}`} replace />;
};

function App() {
  return (
    <HelmetProvider>
      <I18nProvider>
        <div className="App">
          <BrowserRouter>
            <CollapseSlashes />
            <ScrollToTop />
            <HashAnchorScroller />
            <AnalyticsListener />
            <LangParamRedirect />
            <Hreflang />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* English (default) — no path prefix. */}
                {appRoutes}
                {/* All other languages — `/{lang}/{anything}`. */}
                <Route path="/:lang/*" element={<LangGuard />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Suspense fallback={null}>
            <Toaster position="bottom-right" />
          </Suspense>
          <Suspense fallback={null}>
            <VpnBlockOverlay />
          </Suspense>
        </div>
      </I18nProvider>
    </HelmetProvider>
  );
}

export default App;
